import { Alert, Box, Button, Typography, styled } from "@mui/material";
import { ReactElement, useEffect, useRef, useState } from "react";

import {
  GetInvalidContactsSignedDownloadUrlMutation,
  GetInvalidContactsSignedDownloadUrlMutationVariables,
  GetStagedContactsSignedDownloadUrlMutation,
  GetStagedContactsSignedDownloadUrlMutationVariables,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import { LinkButton } from "~frontend/components/Link";
import { convertGraphQLToTargetingForm } from "~frontend/components/audienceFilteringForm";
import {
  GET_INVALID_CONTACTS_SIGNED_DOWNLOAD_URL,
  GET_STAGED_CONTACTS_SIGNED_DOWNLOAD_URL,
} from "~frontend/graphql/campaigns";
import { useActiveCampaigns } from "~frontend/hooks/useActiveCampaigns";
import theme, { palette } from "~frontend/theme";
import determineFilename from "~frontend/util/determineImportFilename";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import { generateTargetGroupSentence } from "~frontend/util/generateTargetGroupSentence";
import { plural } from "~frontend/util/plural";
import useMutation from "~frontend/util/useMutation";

import FollowUpCampaignFilterSummary from "../campaignEditorUtils/FollowUpCampaignFilterSummary";
import { CampaignForEditor } from "../types";

const NoBulletList = styled("ul")({
  listStyleType: "none",
});

function DownloadProgressBox({
  downloadLinkRef,
}: {
  downloadLinkRef: React.RefObject<HTMLAnchorElement | null>;
}): ReactElement {
  return (
    <FlexBox direction="column" alignItems="flex-end">
      <Typography variant="label2Caps" sx={{}}>
        Downloading...
      </Typography>
      <Typography variant="body3">
        {`Your download will begin shortly. If it does not, `}
        <LinkButton
          onClick={void downloadLinkRef.current?.click()}
          sx={{ display: "inline", textWrap: "nowrap" }}
        >
          click here
        </LinkButton>
        .
      </Typography>
    </FlexBox>
  );
}

export default function JobResult({
  campaign,
  job,
}: {
  campaign: CampaignForEditor;
  job: CampaignForEditor["contactImportJob"];
}): ReactElement {
  const { campaignNamesById } = useActiveCampaigns();

  const result = job?.result;

  const downloadLinkRef = useRef<HTMLAnchorElement | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string>();
  const [isDownloading, setIsDownloading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const formatError = useErrorMessageFormatter();

  const [getStagedContactsSignedDownloadUrl] = useMutation<
    GetStagedContactsSignedDownloadUrlMutation,
    GetStagedContactsSignedDownloadUrlMutationVariables
  >(GET_STAGED_CONTACTS_SIGNED_DOWNLOAD_URL, {
    variables: {
      input: {
        campaignId: campaign.id,
      },
    },
  });

  const [getInvalidContactsSignedDownloadUrl] = useMutation<
    GetInvalidContactsSignedDownloadUrlMutation,
    GetInvalidContactsSignedDownloadUrlMutationVariables
  >(GET_INVALID_CONTACTS_SIGNED_DOWNLOAD_URL, {
    variables: {
      input: {
        campaignId: campaign.id,
      },
    },
  });

  useEffect(() => {
    downloadLinkRef.current?.click();
    setTimeout(() => {
      setIsDownloading(false);
    }, 2000);
  }, [downloadUrl]);

  if (!result) {
    return <></>;
  }

  const fileName = determineFilename(campaign.contactImportJob);

  const nRemovedDuringUpload =
    result.nInvalid +
    result.nDuplicates +
    (result.nOptOuts ?? 0) +
    (result.nFilteredOut ?? 0) +
    (result.nLandlines ?? 0) +
    (result.nDoNotCall ?? 0) +
    (result.nNotSubscribed ?? 0);

  const originalFileSize = result.nContacts + nRemovedDuringUpload;
  const contactsAfterSuppressions =
    result.nContacts - campaign.suppressedContactCount;

  const invalidAndDNC = result.nInvalid + (result.nDoNotCall ?? 0);

  const ngpVanSavedListCount =
    result.__typename === "CampaignContactNgpVanImportResult"
      ? result.sourceList.listCount
      : 0;

  const nNgpVanFiltered =
    ngpVanSavedListCount > originalFileSize
      ? ngpVanSavedListCount - originalFileSize
      : 0;

  const objectLabel = campaign.usingOptInStrategy
    ? "subscribers who"
    : "contacts who";

  return (
    <Box>
      {contactsAfterSuppressions <= 0 && (
        <Alert severity="error" sx={{ mb: 1 }}>
          Campaign has no contacts
        </Alert>
      )}
      <Typography variant="body1">
        {originalFileSize} contacts loaded from {fileName}
        {job.__typename === "CampaignContactFollowUpImportJob" && (
          <>
            {" "}
            <FollowUpCampaignFilterSummary jobArgs={job.args} />
          </>
        )}
      </Typography>
      {job.__typename === "CampaignContactFollowUpImportJob" && job.result && (
        <FlexBox justifyContent="space-between" alignItems="flex-end">
          <NoBulletList>
            {job.result.sourceCampaigns.map(sourceCampaign => (
              <Typography
                variant="body2"
                component="li"
                key={sourceCampaign.campaignId}
              >
                {sourceCampaign.nContacts.toLocaleString()} contacts from{" "}
                {sourceCampaign.campaignName}
              </Typography>
            ))}
          </NoBulletList>
        </FlexBox>
      )}
      {job.__typename === "CampaignContactAudienceImportJob" &&
        job.args &&
        job.args.filter && (
          <FlexBox justifyContent="space-between" alignItems="flex-end">
            <NoBulletList sx={{ marginBottom: 0 }}>
              {/* TODO: check for legacy filter */}
              {convertGraphQLToTargetingForm(job.args.filter).targetGroups.map(
                (targetGroup, idx) => {
                  const savedSearchTargetGroups =
                    targetGroup.clauses[0]?.filterConfig?.searchBody
                      ?.targetGroups;

                  return (
                    <Typography
                      key={`target-summary-${idx}`}
                      variant="body2"
                      component="li"
                      sx={{ mb: 1 }}
                    >
                      {idx > 0 ? "OR " : ""}
                      Filter Group {idx + 1}:{" "}
                      {savedSearchTargetGroups
                        ? savedSearchTargetGroups
                            .map(tg =>
                              generateTargetGroupSentence(
                                tg.clauses,
                                campaignNamesById,
                                objectLabel
                              )
                            )
                            .join(" OR ")
                        : generateTargetGroupSentence(
                            targetGroup.clauses,
                            campaignNamesById,
                            objectLabel
                          )}
                    </Typography>
                  );
                }
              )}
            </NoBulletList>
          </FlexBox>
        )}
      {(nRemovedDuringUpload > 0 ||
        campaign.suppressedContactCount > 0 ||
        nNgpVanFiltered > 0) && (
        <FlexBox justifyContent="space-between" alignItems="flex-end">
          <NoBulletList>
            {nNgpVanFiltered > 0 && (
              <Typography variant="body2" component="li">
                {`${nNgpVanFiltered.toLocaleString()} removed by VAN (original list size was ${ngpVanSavedListCount.toLocaleString()})`}
              </Typography>
            )}
            {invalidAndDNC ? (
              <Typography variant="body2" component="li">
                {plural(invalidAndDNC, "contact", "contacts")} skipped due to
                invalid phone numbers
              </Typography>
            ) : null}
            {result.nDuplicates ? (
              <Typography variant="body2" component="li">
                {plural(result.nDuplicates, "duplicate", "duplicates")} skipped
              </Typography>
            ) : null}
            {result.nOptOuts ? (
              <Typography variant="body2" component="li">
                {plural(
                  result.nOptOuts,
                  "opted-out contact",
                  "opted-out contacts"
                )}{" "}
                skipped
              </Typography>
            ) : null}
            {result.nFilteredOut ? (
              <Typography variant="body2" component="li">
                {`${result.nFilteredOut.toLocaleString()} filtered out`}
              </Typography>
            ) : null}
            {result.nLandlines ? (
              <Typography variant="body2" component="li">
                {plural(
                  result.nLandlines,
                  "landline number",
                  "landline numbers"
                )}{" "}
                skipped
              </Typography>
            ) : null}
            {result.nNotSubscribed ? (
              <Typography variant="body2" component="li">
                {plural(
                  result.nNotSubscribed,
                  "unsubscribed contact filtered out",
                  "unsubscribed contacts filtered out"
                )}
              </Typography>
            ) : null}
            {campaign.suppressedContactCount ? (
              <Typography variant="body2" component="li">
                {plural(campaign.suppressedContactCount, "contact", "contacts")}{" "}
                removed by suppressions and overlap management
              </Typography>
            ) : null}
          </NoBulletList>
          {campaign.contactImportJob?.result
            ?.hasInvalidContactsSignedDownloadUrl && (
            <Box>
              <LinkButton
                sx={{
                  fontSize: theme.spacing(1.25),
                  display: isDownloading ? "none" : "auto",
                }}
                color={"secondary"}
                onClick={async () => {
                  if (downloadLinkRef.current) {
                    setIsDownloading(true);
                    const result = await getInvalidContactsSignedDownloadUrl();
                    if (result.errors) {
                      setErrorMessage(result.errors.join(", "));
                      throw new Error(formatError(result.errors));
                    }
                    setDownloadUrl(
                      result.data!.getInvalidContactsSignedDownloadUrl
                    );
                  }
                }}
              >
                Download Invalid Numbers
              </LinkButton>
            </Box>
          )}
        </FlexBox>
      )}

      <FlexBox
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          borderTop: "1px solid",
          borderTopColor: palette.lightestGray,
          paddingTop: 2,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <strong>
            {contactsAfterSuppressions.toLocaleString()} total contacts in
            campaign
          </strong>
        </Box>
        {campaign.contactImportJob?.result
          ?.hasStagedContactsSignedDownloadUrl && (
          <Box>
            <Button
              variant={"contained"}
              color={"secondary"}
              onClick={async () => {
                if (downloadLinkRef.current) {
                  setIsDownloading(true);
                  const result = await getStagedContactsSignedDownloadUrl();
                  if (result.errors) {
                    setErrorMessage(result.errors.join(", "));
                    throw new Error(formatError(result.errors));
                  }
                  setDownloadUrl(
                    result.data!.getStagedContactsSignedDownloadUrl
                  );
                }
              }}
              sx={{
                fontSize: "10px",
                padding: "6px 8px",
                height: "auto",
                display: isDownloading ? "none" : "auto",
              }}
            >
              Download List
            </Button>
            {isDownloading && (
              <DownloadProgressBox downloadLinkRef={downloadLinkRef} />
            )}
          </Box>
        )}
      </FlexBox>
      {errorMessage && (
        <Alert severity="error" sx={{ marginTop: 1 }}>
          {errorMessage}
        </Alert>
      )}

      <a ref={downloadLinkRef} href={downloadUrl} aria-disabled={true} />
    </Box>
  );
}
